import * as THREE from 'three';
import type { AvatarAppearance } from '../../shared/protocol.ts';
import { createAvatar, cube, matte } from './assets.ts';

export type AvatarVisual = {
  id:string; appearance:AvatarAppearance; x:number; y:number; z:number; yaw:number;
  scale:number; moving:boolean; cheering:boolean; pie:boolean; visible:boolean; frozen?:boolean;
};
type Piece={parent:THREE.Object3D;local:THREE.Matrix4;color:THREE.Color;pie:boolean};
type Model={id:string;signature:string;avatar:ReturnType<typeof createAvatar>;pieces:Piece[];offset:number;phase:number;dirty:boolean;pose:Float64Array;wasStatic:boolean};
const INSTANCE_BUDGET=170;

/** Packed geometry: one draw call, no 170-instance holes, no per-delta color reuploads. */
export class AvatarBatch {
  readonly mesh:THREE.InstancedMesh;
  private models=new Map<string,Model>();
  private order:Model[]=[];
  private readonly matrix=new THREE.Matrix4();
  private readonly hidden=new THREE.Matrix4().makeScale(0,0,0);
  constructor(private readonly maximum:number){
    this.mesh=new THREE.InstancedMesh(cube,matte,maximum*INSTANCE_BUDGET);
    this.mesh.name='Packed party guests';this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.castShadow=false;this.mesh.receiveShadow=false;
    this.mesh.boundingSphere=new THREE.Sphere(new THREE.Vector3(0,2,0),13);
    this.mesh.boundingBox=new THREE.Box3(new THREE.Vector3(-8,-1,-7),new THREE.Vector3(8,7,7));
    this.mesh.setColorAt(0,new THREE.Color());this.mesh.count=0;
  }
  sync(visuals:AvatarVisual[]){
    const incoming=new Set(visuals.slice(0,this.maximum).map(v=>v.id));let changed=false;
    for(const [id,model]of this.models)if(!incoming.has(id)){this.disposeModel(model);this.models.delete(id);changed=true;}
    const next:Model[]=[];
    for(const visual of visuals.slice(0,this.maximum)){
      const signature=JSON.stringify(visual.appearance);let model=this.models.get(visual.id);
      if(!model||model.signature!==signature){
        if(model)this.disposeModel(model);
        const avatar=createAvatar(visual.appearance);avatar.group.updateMatrixWorld(true);const pieces:Piece[]=[];
        avatar.group.traverse(object=>{
          if(!(object instanceof THREE.InstancedMesh))return;
          for(let i=0;i<object.count;i++){
            const local=new THREE.Matrix4(),color=new THREE.Color();object.getMatrixAt(i,local);object.getColorAt(i,color);
            pieces.push({parent:object,local,color,pie:false});
          }
        });
        const head=avatar.group.getObjectByName('head')!;
        for(const [x,y,z,w,h,d]of [[0,.22,.262,.34,.16,.065],[-.13,.30,.263,.12,.13,.055],[.13,.19,.274,.13,.18,.075],[.025,.345,.257,.19,.085,.055],[-.045,.14,.276,.17,.07,.05]]){
          const local=new THREE.Matrix4().compose(new THREE.Vector3(x,y,z),new THREE.Quaternion(),new THREE.Vector3(w,h,d));
          pieces.push({parent:head,local,color:new THREE.Color('#fff2d9'),pie:true});
        }
        if(pieces.length>INSTANCE_BUDGET)throw new Error('Avatar exceeds its bounded geometry budget.');
        let seed=0;for(const c of visual.id)seed=(seed*31+c.charCodeAt(0))>>>0;
        model={id:visual.id,signature,avatar,pieces,offset:0,phase:(seed%1000)/137,dirty:true,pose:new Float64Array(7),wasStatic:false};
        this.models.set(visual.id,model);changed=true;
      }
      next.push(model);if(this.order[next.length-1]!==model)changed=true;
    }
    if(!changed&&next.length===this.order.length)return;
    this.order=next;let offset=0;
    for(const model of this.order){
      model.offset=offset;model.dirty=true;
      for(const piece of model.pieces){this.mesh.setMatrixAt(offset,this.hidden);this.mesh.setColorAt(offset,piece.color);offset++;}
    }
    this.mesh.count=offset;
    this.mesh.instanceColor!.clearUpdateRanges();this.mesh.instanceColor!.addUpdateRange(0,Math.max(1,offset)*3);this.mesh.instanceColor!.needsUpdate=true;
    this.mesh.instanceMatrix.clearUpdateRanges();this.mesh.instanceMatrix.addUpdateRange(0,Math.max(1,offset)*16);this.mesh.instanceMatrix.needsUpdate=true;
  }
  update(visuals:AvatarVisual[],timeSeconds:number,reducedMotion:boolean){
    this.mesh.instanceMatrix.clearUpdateRanges();let dirty=false;
    for(let m=0;m<this.order.length;m++){
      const model=this.order[m],visual=visuals[m];if(!visual)continue;
      // Offline geometry is retained, but does not burn animation CPU or upload bandwidth.
      const stationary=!!visual.frozen||reducedMotion||!visual.visible;
      const pose=model.pose;
      if(stationary&&model.wasStatic&&!model.dirty&&pose[0]===visual.x&&pose[1]===visual.y&&pose[2]===visual.z&&pose[3]===visual.yaw&&pose[4]===visual.scale&&pose[5]===Number(visual.pie)&&pose[6]===Number(visual.visible))continue;
      if(stationary){pose[0]=visual.x;pose[1]=visual.y;pose[2]=visual.z;pose[3]=visual.yaw;pose[4]=visual.scale;pose[5]=Number(visual.pie);pose[6]=Number(visual.visible);}
      model.wasStatic=stationary;model.dirty=false;dirty=true;
      if(!visual.visible){for(let i=0;i<model.pieces.length;i++)this.mesh.setMatrixAt(model.offset+i,this.hidden);}
      else {
        const group=model.avatar.group;
        group.position.set(visual.x,visual.y,visual.z);group.rotation.y=visual.yaw;group.scale.setScalar(visual.scale);
        model.avatar.animate(stationary?0:timeSeconds+model.phase,visual.moving&&!stationary,visual.cheering&&!stationary);
        group.updateMatrixWorld(true);
        for(let i=0;i<model.pieces.length;i++){
          const piece=model.pieces[i];
          if(piece.pie&&!visual.pie)this.mesh.setMatrixAt(model.offset+i,this.hidden);
          else {this.matrix.multiplyMatrices(piece.parent.matrixWorld,piece.local);this.mesh.setMatrixAt(model.offset+i,this.matrix);}
        }
      }
      this.mesh.instanceMatrix.addUpdateRange(model.offset*16,model.pieces.length*16);
    }
    if(dirty)this.mesh.instanceMatrix.needsUpdate=true;
  }
  entityAt(instanceId:number|undefined):string|undefined {
    if(instanceId===undefined||instanceId<0||instanceId>=this.mesh.count)return;
    let lo=0,hi=this.order.length-1;
    while(lo<=hi){const mid=(lo+hi)>>>1,m=this.order[mid];if(instanceId<m.offset)hi=mid-1;else if(instanceId>=m.offset+m.pieces.length)lo=mid+1;else return m.id.startsWith('__')?undefined:m.id;}
  }
  private disposeModel(model:Model){model.avatar.group.traverse(o=>{if(o instanceof THREE.InstancedMesh)o.dispose();});}
  dispose(){for(const model of this.models.values())this.disposeModel(model);this.models.clear();this.order=[];this.mesh.dispose();}
}
